import React, { useState, useRef } from 'react';
import { Plus, Pencil, Trash2, X, Upload, Save, Star, ChevronDown } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import AdminLayout from '../../components/AdminLayout';

const DURATION_MASTER = [1, 2, 3, 4, 5, 6, 8];

const blank = {
  id: '', name: '', rating: 4.8, reviews: 100,
  price: 299, oldPrice: 499, desc: '', image: '', save: 40,
  pricing_type: 'fixed', hourly_rate: '', min_hours: 1,
  allowed_hours: [...DURATION_MASTER],
};

const AdminServices = () => {
  const { categories, services, upsertService, deleteService } = useApp();
  const [selectedCat, setSelectedCat] = useState(categories[0]?.id || '');
  const [editing, setEditing] = useState(null);
  const fileRef = useRef();

  const catData = services[selectedCat] || { title: '', items: [] };

  const openNew = () => setEditing({ ...blank, id: 's_' + Date.now(), isNew: true });
  const openEdit = (it) => setEditing({
    ...it,
    isNew: false,
    allowed_hours: (it.allowed_hours && it.allowed_hours.length ? it.allowed_hours : [...DURATION_MASTER]),
  });
  const close = () => setEditing(null);

  const toggleHour = (h) => {
    const cur = new Set(editing.allowed_hours || []);
    cur.has(h) ? cur.delete(h) : cur.add(h);
    setEditing({ ...editing, allowed_hours: Array.from(cur).sort((a, b) => a - b) });
  };

  const save = () => {
    if (!editing.name.trim()) { alert('Name is required'); return; }
    const isHourly = editing.pricing_type === 'hourly';
    if (isHourly && !Number(editing.hourly_rate)) {
      alert('Hourly rate is required for an hourly service'); return;
    }
    if (isHourly && (!editing.allowed_hours || editing.allowed_hours.length === 0)) {
      alert('Select at least one allowed duration for an hourly service'); return;
    }
    const minH = Math.max(1, Number(editing.min_hours) || 1);
    if (isHourly && !editing.allowed_hours.some((h) => h >= minH)) {
      alert(`No allowed duration is ≥ minimum hours (${minH}). Fix Min Hours or pick a longer duration.`); return;
    }
    const { isNew, ...item } = editing;
    upsertService(selectedCat, {
      ...item,
      pricing_type: isHourly ? 'hourly' : 'fixed',
      hourly_rate:  isHourly ? Number(item.hourly_rate) || 0 : null,
      min_hours:    isHourly ? minH : 1,
      allowed_hours: isHourly ? item.allowed_hours.filter((h) => h >= minH) : null,
      price:    isHourly ? Number(item.hourly_rate) || 0 : Number(item.price) || 0,
      oldPrice: Number(item.oldPrice) || 0,
      rating:   Number(item.rating) || 0,
      reviews:  Number(item.reviews) || 0,
      save:     Number(item.save) || 0,
    });
    close();
  };

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setEditing((prev) => ({ ...prev, image: reader.result }));
    reader.readAsDataURL(file);
  };

  const remove = (id) => {
    if (window.confirm('Delete this service?')) deleteService(selectedCat, id);
  };

  return (
    <AdminLayout title="Services">
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-5">
        <div className="relative">
          <select value={selectedCat} onChange={(e) => setSelectedCat(e.target.value)}
            className="appearance-none pl-4 pr-10 py-2.5 rounded-lg border border-neutral-200 bg-white font-semibold text-sm focus:outline-none focus:border-[#2E7D32]">
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none" />
        </div>
        <p className="text-sm text-neutral-500">{catData.items?.length || 0} service(s) in this category</p>
        <button onClick={openNew} className="md:ml-auto inline-flex items-center gap-2 bg-[#2E7D32] hover:bg-[#276D2A] text-white px-4 py-2 rounded-lg font-semibold text-sm transition-colors">
          <Plus className="w-4 h-4" /> Add Service
        </button>
      </div>

      <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr>
              <th className="text-left px-4 py-3 font-semibold text-neutral-700">Service</th>
              <th className="text-left px-4 py-3 font-semibold text-neutral-700 hidden md:table-cell">Rating</th>
              <th className="text-left px-4 py-3 font-semibold text-neutral-700">Price</th>
              <th className="text-right px-4 py-3 font-semibold text-neutral-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(catData.items || []).map((it) => (
              <tr key={it.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <img src={it.image} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{it.name}</p>
                      <p className="text-xs text-neutral-500 truncate max-w-xs">{it.desc}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 hidden md:table-cell">
                  <div className="flex items-center gap-1">
                    <Star className="w-3.5 h-3.5 fill-[#F5B301] text-[#F5B301]" />
                    <span className="font-semibold">{it.rating}</span>
                    <span className="text-xs text-neutral-500">({it.reviews})</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  {it.pricing_type === 'hourly' ? (
                    <>
                      <p className="font-bold text-[#EA580C]" data-testid={`svc-price-${it.id}`}>₹{it.hourly_rate || it.price} <span className="text-[11px] text-neutral-500 font-normal">/ hr</span></p>
                      <p className="text-[10px] text-neutral-500">min {it.min_hours || 1} hr</p>
                    </>
                  ) : (
                    <>
                      <p className="font-bold text-[#EA580C]" data-testid={`svc-price-${it.id}`}>₹{it.price}</p>
                      {!!it.oldPrice && <p className="text-[11px] text-neutral-400 line-through">₹{it.oldPrice}</p>}
                    </>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1">
                    <button onClick={() => openEdit(it)} className="w-8 h-8 rounded-lg hover:bg-neutral-100 flex items-center justify-center transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => remove(it.id)} className="w-8 h-8 rounded-lg hover:bg-red-50 text-red-600 flex items-center justify-center transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {(!catData.items || catData.items.length === 0) && (
              <tr><td colSpan={4} className="px-4 py-10 text-center text-neutral-500 text-sm">No services in this category yet</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-5 border-b border-neutral-200 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="font-bold text-lg">{editing.isNew ? 'New Service' : 'Edit Service'}</h3>
              <button onClick={close} className="text-neutral-500 hover:text-neutral-900"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs font-semibold text-neutral-700">Image</label>
                <div className="mt-1 flex gap-3">
                  <div className="w-24 h-24 rounded-lg overflow-hidden bg-neutral-100 flex-shrink-0">
                    {editing.image && <img src={editing.image} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input type="text" placeholder="Image URL" value={editing.image}
                      onChange={(e) => setEditing({ ...editing, image: e.target.value })}
                      className="w-full px-3 py-2 text-sm rounded-lg border border-neutral-200 focus:outline-none focus:border-[#2E7D32]" />
                    <input ref={fileRef} type="file" accept="image/*" onChange={onPickFile} className="hidden" />
                    <button onClick={() => fileRef.current?.click()} className="w-full inline-flex items-center justify-center gap-2 py-2 rounded-lg bg-neutral-100 hover:bg-neutral-200 text-sm font-semibold text-neutral-700 transition-colors">
                      <Upload className="w-3.5 h-3.5" /> Upload
                    </button>
                  </div>
                </div>
              </div>

              <Field label="Service name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} />
              <div>
                <label className="text-xs font-semibold text-neutral-700">Description</label>
                <textarea value={editing.desc} onChange={(e) => setEditing({ ...editing, desc: e.target.value })} rows={2}
                  className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-neutral-200 focus:outline-none focus:border-[#2E7D32]" />
              </div>

              {/* Pricing type toggle */}
              <div>
                <label className="text-xs font-semibold text-neutral-700 mb-1 block">Pricing type</label>
                <div className="inline-flex bg-neutral-100 rounded-lg p-1">
                  <button type="button" data-testid="svc-pricing-fixed"
                    onClick={() => setEditing({ ...editing, pricing_type: 'fixed' })}
                    className={`px-4 py-1.5 text-xs font-semibold rounded-md ${(editing.pricing_type || 'fixed') === 'fixed' ? 'bg-white text-[#2E7D32] shadow-sm' : 'text-neutral-600'}`}>
                    Fixed price
                  </button>
                  <button type="button" data-testid="svc-pricing-hourly"
                    onClick={() => setEditing({ ...editing, pricing_type: 'hourly' })}
                    className={`px-4 py-1.5 text-xs font-semibold rounded-md ${editing.pricing_type === 'hourly' ? 'bg-white text-[#2E7D32] shadow-sm' : 'text-neutral-600'}`}>
                    Hourly rate
                  </button>
                </div>
                <p className="mt-1 text-[11px] text-neutral-500">
                  {editing.pricing_type === 'hourly'
                    ? 'Customers pick a duration; total is rate × hours.'
                    : 'Flat one-time price for the whole service.'}
                </p>
              </div>

              {editing.pricing_type === 'hourly' ? (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <Field
                      label="Base hourly rate (₹/hr)"
                      type="number"
                      value={editing.hourly_rate}
                      onChange={(v) => setEditing({ ...editing, hourly_rate: v })}
                      testid="svc-hourly-rate"
                    />
                    <Field
                      label="Minimum hours"
                      type="number"
                      value={editing.min_hours || 1}
                      onChange={(v) => setEditing({ ...editing, min_hours: v })}
                      testid="svc-min-hours"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-neutral-700 mb-1 block">Allowed durations (customer will pick from these)</label>
                    <div className="flex flex-wrap gap-2" data-testid="svc-allowed-hours">
                      {DURATION_MASTER.map((h) => {
                        const active = (editing.allowed_hours || []).includes(h);
                        const belowMin = h < (Number(editing.min_hours) || 1);
                        return (
                          <button
                            key={h}
                            type="button"
                            data-testid={`svc-allowed-${h}`}
                            onClick={() => !belowMin && toggleHour(h)}
                            disabled={belowMin}
                            title={belowMin ? 'Below minimum hours' : ''}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${
                              belowMin ? 'bg-neutral-50 border-neutral-100 text-neutral-300 cursor-not-allowed line-through' :
                              active ? 'bg-[#2E7D32] text-white border-[#2E7D32]' :
                              'bg-white text-neutral-700 border-neutral-200 hover:border-[#2E7D32]'
                            }`}>
                            {h}h
                          </button>
                        );
                      })}
                    </div>
                    <p className="mt-1 text-[11px] text-neutral-500">Customers will only be able to book these exact durations. Live total = rate × hours.</p>
                  </div>
                </>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Price (₹)" type="number" value={editing.price} onChange={(v) => setEditing({ ...editing, price: v })} />
                  <Field label="Old price (₹)" type="number" value={editing.oldPrice} onChange={(v) => setEditing({ ...editing, oldPrice: v })} />
                </div>
              )}

              <div className="grid grid-cols-3 gap-3">
                <Field label="Rating" type="number" value={editing.rating} onChange={(v) => setEditing({ ...editing, rating: v })} />
                <Field label="Reviews" type="number" value={editing.reviews} onChange={(v) => setEditing({ ...editing, reviews: v })} />
                <Field label="Save %" type="number" value={editing.save} onChange={(v) => setEditing({ ...editing, save: v })} />
              </div>
            </div>
            <div className="p-5 border-t border-neutral-200 flex justify-end gap-2 sticky bottom-0 bg-white">
              <button onClick={close} className="px-4 py-2 rounded-lg text-sm font-semibold text-neutral-700 hover:bg-neutral-100 transition-colors">Cancel</button>
              <button onClick={save} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#2E7D32] hover:bg-[#276D2A] text-white text-sm font-semibold transition-colors">
                <Save className="w-4 h-4" /> Save
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

const Field = ({ label, type = 'text', value, onChange, testid }) => (
  <div>
    <label className="text-xs font-semibold text-neutral-700">{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-neutral-200 focus:outline-none focus:border-[#2E7D32]" />
  </div>
);

export default AdminServices;
